import './styles/index.scss';
import 'core-js/stable';

// Lazy load non-critical CSS to improve initial load time
// These are loaded async and applied when ready
// @ts-ignore - CSS modules don't have type declarations
import('react-toastify/dist/ReactToastify.min.css');
// @ts-ignore - CSS modules don't have type declarations
import('slick-carousel/slick/slick.css');
// @ts-ignore - CSS modules don't have type declarations
import('slick-carousel/slick/slick-theme.css');

import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { ToastContainer } from 'react-toastify';
import App from './app';
import { Auth0Provider } from './react-auth0-spa';
import history from './helpers/history';
import queryString from 'query-string';
import {
  HOME_ROUTE,
  INVITATION_ROUTE,
  WORKPLACE_ROUTE,
} from 'constants/routes';
import store from './configure-store';

declare const AUTH0_DOMAIN: string;
declare const AUTH0_CLIENT_ID: string;

interface IExtraParams {
  prompt: string;
  email: string;
  targetUrl: string;
  fn?: string;
  ln?: string;
  insuranceTypes?: string;
  insuranceReasons?: string;
  premium?: string;
  hideSocial?: boolean;
  adviserSignUp?: boolean;
  phone?: string;
  fspNo?: string;
  company?: string;
  utmSource?: string;
  utmCampaign?: string;
  utmMedium?: string;
  utmContent?: string;
}

const removeCodeAndStateQuery = (url: string) =>
  queryString.exclude(url, ['code', 'state']);

const getRedirectUrl = () => {
  return queryString.stringifyUrl({
    url: `${window.location.origin}/callback`,
    query: queryString.parse(window.location.search),
  });
};

const onRedirectCallback = (appState?: { targetUrl?: string }) => {
  const parsed = queryString.parse(window.location.search);
  let pathname = appState?.targetUrl || window.location.pathname;

  if (pathname.includes('onboarding')) {
    pathname = HOME_ROUTE;
  }

  if (parsed.q4eId) {
    pathname = `${WORKPLACE_ROUTE}?id=${parsed.q4eId}`;
  } else if (parsed.redirectPath) {
    pathname = parsed.redirectPath;
  }

  const query = removeCodeAndStateQuery(window.location.search);
  history.replace(
    `${pathname}${pathname.includes('?') ? query.replace('?', '&') : query}`,
  );
};

const initApp = () => {
  const container = document.getElementById('app');
  if (!container) return;

  const root = createRoot(container);
  const isInvitationPath = window.location.pathname.includes(INVITATION_ROUTE);
  const isOnboardingPath = window.location.pathname.includes('onboarding');
  const redirectUrl = getRedirectUrl();
  const extraParams: IExtraParams = {
    prompt: 'login',
    email: '',
    targetUrl: window.location.pathname,
  };

  const parsed = queryString.parse(window.location.search);

  if (isInvitationPath) {
    Object.assign(extraParams, {
      prompt: parsed.prompt as string,
      email: parsed.email as string,
      hideSocial: true,
    });
  } else if (isOnboardingPath) {
    Object.assign(extraParams, {
      prompt: parsed.prompt as string,
      fn: parsed.fn as string,
      ln: parsed.ln as string,
      insuranceTypes: parsed.insuranceTypes as string,
      insuranceReasons: parsed.insuranceReasons as string,
      premium: parsed.premium as string,
      utmSource: parsed.utmSource as string,
      utmCampaign: parsed.utmCampaign as string,
      utmMedium: parsed.utmMedium as string,
      utmContent: parsed.utmContent as string,
    });

    if (parsed.adviserSignUp === 'true') {
      Object.assign(extraParams, {
        adviserSignUp: true,
        email: parsed.email as string,
        phone: parsed.phone as string,
        fspNo: parsed.fspNo as string,
        company: parsed.company as string,
        hideSocial: true,
      });
    }
  }

  if (extraParams.prompt !== 'login') {
    extraParams.prompt = '';
  }

  const isMobile = window.innerWidth < 768;

  root.render(
    <Auth0Provider
      domain={AUTH0_DOMAIN}
      client_id={AUTH0_CLIENT_ID}
      redirect_uri={redirectUrl}
      onRedirectCallback={onRedirectCallback}
      cacheLocation="memory"
      {...extraParams}
    >
      <Provider store={store}>
        <App />
        <ToastContainer
          position="bottom-right"
          autoClose={8000}
          draggable={true}
          hideProgressBar={true}
          closeButton={false}
          closeOnClick={false}
          limit={isMobile ? 2 : 3}
        />
      </Provider>
    </Auth0Provider>,
  );
};

// Initialize the app when DOM is ready (don't wait for all resources to load)
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initApp);
} else {
  // DOM is already ready
  initApp();
}
